import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AccountSelectScreen } from '@/components/custom-dashboard/AccountSelectScreen';
import { AccountSwitcher } from '@/components/custom-dashboard/AccountSwitcher';
import { useAccountFavorites } from '@/components/custom-dashboard/hooks/useAccountFavorites';
import { useAccountIdParam } from '@/components/custom-dashboard/hooks/useAccountIdParam';
import { CustomDashboardPresentation } from '@/components/custom-dashboard/CustomDashboardPresentation';
import { TabConfigScreen } from '@/components/custom-dashboard/TabConfigScreen';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuthHeader } from '@/hooks/useAuth';
import { getCustomDashboardTabs } from '@/lib/api/custom-dashboard';
import { useAccounts } from '@/lib/query/hooks/useAccounts';

export function CustomDashboardPage() {
  const { data: accounts = [], isLoading: isAccountsLoading, error: accountsError } = useAccounts();
  const { favoriteAccountIds, toggleFavorite } = useAccountFavorites();
  const { accountId } = useAccountIdParam();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getAuthHeader } = useAuthHeader();
  const authHeaders = useMemo(() => getAuthHeader(), [getAuthHeader]);
  const [isNavigationBlocked, setIsNavigationBlocked] = useState(false);
  const [isEditingTabs, setIsEditingTabs] = useState(false);
  const isKnownAccount =
    isAccountsLoading || accounts.some((account) => account.code === accountId);
  const verifiedAccountId = isKnownAccount ? accountId : '';

  const tabsQuery = useQuery({
    queryKey: ['custom-dashboard-tabs', verifiedAccountId],
    queryFn: ({ signal }) => getCustomDashboardTabs(verifiedAccountId, authHeaders, signal),
    enabled: verifiedAccountId.length > 0,
  });
  const availableTabs = tabsQuery.data?.tabs ?? [];
  // URL の ?tab= が利用可能タブを指していればそれを、それ以外は先頭グループの先頭タブを採用する
  // （BE は tabs をグループの順 → グループ内の順に返す）。
  const tabParam = searchParams.get('tab');
  const requestedTab = tabParam ? availableTabs.find((tab) => tab.tabId === tabParam) : undefined;
  const activeTab = requestedTab ?? availableTabs[0];
  // 未設定（tabs空）・全タブ利用不可のいずれも「表示できるタブが無い」ため設定画面に誘導する。
  const needsTabConfig = tabsQuery.data !== undefined && activeTab === undefined;

  // 不明・削除済み・利用不可な ?tab= は先頭の利用可能タブへ replace で正規化する（履歴は汚さない）。
  // tab 未指定はそのまま（正規化不要）。
  useEffect(() => {
    if (tabParam === null || requestedTab !== undefined) return;
    if (availableTabs.length === 0) return;
    const next = new URLSearchParams(searchParams);
    next.set('tab', availableTabs[0].tabId);
    setSearchParams(next, { replace: true });
  }, [tabParam, requestedTab, availableTabs, searchParams, setSearchParams]);

  const handleTabChange = useCallback(
    (tabId: string) => {
      const next = new URLSearchParams(searchParams);
      next.set('tab', tabId);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // アカウント切替時は前アカウントの ?tab= を持ち越さない。
  const handleSelectAccount = useCallback(
    (nextAccountId: string) => {
      const next = new URLSearchParams(searchParams);
      next.set('account', nextAccountId);
      next.delete('tab');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleNavigation = useCallback(() => {
    setIsNavigationBlocked(true);
    toast.error('ダッシュボード内の画面遷移を検知したため表示を停止しました');
  }, []);

  if (!accountId || !isKnownAccount) {
    return (
      <AppLayout title="カスタムダッシュボード" showSearch={false}>
        <AccountSelectScreen
          accounts={accounts}
          isLoading={isAccountsLoading}
          error={accountsError}
          onSelect={handleSelectAccount}
          favoriteAccountIds={favoriteAccountIds}
          onToggleFavorite={toggleFavorite}
        />
      </AppLayout>
    );
  }

  const showTabConfig = needsTabConfig || isEditingTabs;
  const isLoadingTabs = tabsQuery.isLoading;
  // data保持中（キャッシュ済みタブ構成がある）background refetch失敗では全画面エラーにしない。
  // 使えるデータが無いときだけ画面を占有する。
  const hasTabsError = !isLoadingTabs && tabsQuery.error != null && tabsQuery.data === undefined;

  return (
    <AppLayout title="カスタムダッシュボード" showSearch={false} disableContentPadding>
      <div className="relative flex h-[calc(100vh-var(--app-header-h))] min-w-0">
        <div className="min-w-0 flex-1">
          {isLoadingTabs ? (
            <Skeleton aria-label="読み込み中" className="m-4 h-[calc(100%-2rem)]" />
          ) : hasTabsError ? (
            <div className="flex h-full items-center justify-center p-6">
              <Alert variant="destructive" className="max-w-xl">
                <AlertTitle>タブ構成を取得できませんでした</AlertTitle>
                <AlertDescription className="space-y-4">
                  <p>
                    {tabsQuery.error instanceof Error
                      ? tabsQuery.error.message
                      : String(tabsQuery.error)}
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void tabsQuery.refetch()}
                  >
                    再試行
                  </Button>
                </AlertDescription>
              </Alert>
            </div>
          ) : showTabConfig ? (
            <div className="h-full overflow-y-auto">
              <TabConfigScreen
                accountId={accountId}
                accountName={accounts.find((account) => account.code === accountId)?.name}
                authHeaders={authHeaders}
                onClose={needsTabConfig ? undefined : () => setIsEditingTabs(false)}
              />
            </div>
          ) : (
            <CustomDashboardPresentation
              accountId={accountId}
              isNavigationBlocked={isNavigationBlocked}
              authHeaders={authHeaders}
              onConfigureTabs={() => setIsEditingTabs(true)}
              accountSwitcher={
                <AccountSwitcher
                  accounts={accounts}
                  currentAccountCode={accountId}
                  favoriteAccountIds={favoriteAccountIds}
                  onSelect={handleSelectAccount}
                  onToggleFavorite={toggleFavorite}
                />
              }
              onNavigation={handleNavigation}
              groups={tabsQuery.data?.groups ?? []}
              tabs={tabsQuery.data?.tabs ?? []}
              activeTabId={activeTab?.tabId}
              onTabChange={handleTabChange}
            />
          )}
        </div>
      </div>
    </AppLayout>
  );
}
